import * as z from "zod/mini"

/**
 * Zod-зеркала событийных контрактов бэкенда (contracts/events/*.json).
 *
 * WIRE-особенность: у recommendation-события НЕТ поля type — оно
 * распознаётся по отсутствию type; служебные события несут
 * type=connected|done, все прочие непустые типы считаются «чужими»
 * (например, visit_card_update) и отдаются наружу как есть.
 *
 * Используем zod/mini вместо полного zod: единственный потребитель в
 * проекте, минус ~200 kB исходников в бандле.
 */

export const recommendationItemSchema = z.object({
    priority: z.enum(["high", "medium", "low"]),
    type: z.string(),
    text: z.string(),
    rationale: z.string(),
    suggested_phrase: z.string(),
    confidence: z.number().check(z.minimum(0), z.maximum(1)),
})

export const recommendationEventSchema = z.object({
    session_id: z.string(),
    chunk_index: z.number().check(z.int()),
    elapsed_sec: z.number(),
    recommendations: z.array(recommendationItemSchema),
    current_phase: z.string(),
    prohibitions_violated: z.array(z.string()),
    spin_this_chunk: z.array(z.string()),
    checkpoints: z.record(z.string(), z.number().check(z.int())),
    timestamp: z.string(),
})

const connectedEventSchema = z.object({
    type: z.literal("connected"),
    session_id: z.string(),
})

const doneEventSchema = z.object({
    type: z.literal("done"),
    session_id: z.string(),
})

export type RecommendationItem = z.infer<typeof recommendationItemSchema>
export type RecommendationEvent = z.infer<typeof recommendationEventSchema>

export type CopilotStreamEvent =
    | { kind: "connected" }
    | { kind: "done" }
    | { kind: "recommendations"; event: RecommendationEvent }
    | { kind: "foreign"; type: string; data: Record<string, unknown> }

export function parseCopilotStreamEvent(raw: string): CopilotStreamEvent | null {
    let json: unknown
    try {
        json = JSON.parse(raw)
    } catch {
        return null
    }
    if (typeof json !== "object" || json === null) return null
    const type = (json as { type?: unknown }).type
    if (type === "connected") {
        return connectedEventSchema.safeParse(json).success ? { kind: "connected" } : null
    }
    if (type === "done") {
        return doneEventSchema.safeParse(json).success ? { kind: "done" } : null
    }
    if (typeof type === "string" && type !== "") {
        return { kind: "foreign", type, data: json as Record<string, unknown> }
    }
    const parsed = recommendationEventSchema.safeParse(json)
    if (!parsed.success) return null
    return { kind: "recommendations", event: parsed.data }
}

export function assertNever(value: never): never {
    throw new Error(`unreachable: ${JSON.stringify(value)}`)
}
